import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_BASE_URL } from "../constants";
import { TextBackground } from "../components";
import { List, ListItem, ListItemButton, ListItemText, Typography, Paper, Grid, Box, CircularProgress } from "@mui/material";

// Molstar imports
import { createPluginUI } from "molstar/lib/mol-plugin-ui";
import { renderReact18 } from "molstar/lib/mol-plugin-ui/react18";
import { PluginUIContext } from "molstar/lib/mol-plugin-ui/context";
import { DefaultPluginUISpec } from 'molstar/lib/mol-plugin-ui/spec';
import "molstar/lib/mol-plugin-ui/skin/light.scss";

// Define the types for the complex data and structures
interface StructureData {
    priority: number;
    pdb_file_path: string;
}

interface ComplexDetailData {
    id: string;
    hash_id: string;
    peptide: string;
    alpha_chain: string;
    beta_chain: string;
    mhc_chain: string;
    structures: StructureData[];
}

export const AnalysisViewer = () => {
    const { complex_id } = useParams<{ complex_id: string }>();
    const [complexData, setComplexData] = useState<ComplexDetailData | null>(null);
    const [selectedPdbUrl, setSelectedPdbUrl] = useState<string | null>(null);
    const molstarContainerRef = useRef<HTMLDivElement>(null);
    const molstarPlugin = useRef<PluginUIContext | null>(null);

    // Fetch complex detail data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${BACKEND_BASE_URL}/stegg/complex/${complex_id}/`);
                if (!response.ok) throw new Error("Failed to fetch complex details.");
                const data = await response.json();
                setComplexData(data);
                if (data.structures && data.structures.length > 0) {
                    // Default to loading the highest priority structure
                    setSelectedPdbUrl(data.structures[0].pdb_file_path);
                }
            } catch (error) {
                console.error(error);
                alert("Could not load complex details.");
            }
        };
        if (complex_id) {
            fetchData();
        }
    }, [complex_id]);

    // Initialize and update Molstar view
    useEffect(() => {
        const initMolstar = async () => {
            if (!molstarContainerRef.current) return;
            
            if (!molstarPlugin.current) {
                const spec = DefaultPluginUISpec();
                spec.layout = {
                    initial: { // Use 'initial' to set the starting layout state
                        showControls: false,
                        isExpanded: false,
                        regionState: {
                            top: 'hidden',
                            bottom: 'hidden',
                            left: 'hidden',
                            right: 'hidden',
                        },
                    },
                };                
                molstarPlugin.current = await createPluginUI({
                    target: molstarContainerRef.current,
                    render: renderReact18,
                    spec,
                });                
            }

            if (selectedPdbUrl && molstarPlugin.current) {

                await molstarPlugin.current.clear();
                const data = await molstarPlugin.current.builders.data.download({ url: selectedPdbUrl });
                const trajectory = await molstarPlugin.current.builders.structure.parseTrajectory(data, "pdb");
                await molstarPlugin.current.builders.structure.hierarchy.applyPreset(trajectory, "default");
            }

        };

        initMolstar();

    }, [selectedPdbUrl]);

    if (!complexData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <section className="page">
            <TextBackground>
                <Typography variant="h4" gutterBottom>Analyze Ensemble: {complexData.hash_id}</Typography>
                <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                    <Typography sx={{ wordBreak: 'break-all' }}>
                        <strong>Peptide:</strong> {complexData.peptide}
                    </Typography>
                    <Typography sx={{ wordBreak: 'break-all' }}>
                        <strong>TCR Alpha:</strong> {complexData.alpha_chain}
                    </Typography>
                    <Typography sx={{ wordBreak: 'break-all' }}>
                        <strong>TCR Beta:</strong> {complexData.beta_chain}
                    </Typography>
                </Paper>

                <Grid container spacing={3}>
                    {/* Left Structure List */}
                    <Grid item xs={12} md={3}>
                        <Typography variant="h6">Structures (by priority)</Typography>
                        <Paper elevation={1} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                            <List>
                                {complexData.structures.map((structure) => (
                                    <ListItemButton 
                                        key={structure.priority}
                                        onClick={() => setSelectedPdbUrl(structure.pdb_file_path)}
                                        selected={selectedPdbUrl === structure.pdb_file_path}
                                    >
                                        <ListItemText primary={`Model Rank #${structure.priority}`} />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                    {/* Right 3D View */}
                    <Grid item xs={12} md={9}>
                         <Box 
                            ref={molstarContainerRef} 
                            sx={{ 
                                position: 'relative', 
                                width: '100%', 
                                height: 0, 
                                paddingTop: '100%', /* 1:1 Aspect Ratio */
                                border: '1px solid #ccc' 
                            }} 
                         />
                    </Grid>
                </Grid>
            </TextBackground>
        </section>
    );
};